<template>
  <div v-if="hasOptions">
    <v-expansion-panels variant="accordion">
      <v-expansion-panel
        v-for="(category, key) in options"
        :key="key"
        v-show="category.items.length > 0"
      >
        <v-expansion-panel-title>
          <div class="d-flex align-center">
            <v-icon :icon="category.icon" size="small" class="mr-2" />
            {{ category.title }}
            <v-chip size="x-small" color="primary" variant="tonal" class="ml-2">
              {{ category.items.length }} available
            </v-chip>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-list density="compact">
            <v-list-item v-for="(item, index) in category.items" :key="index">
              <template v-slot:prepend>
                <v-icon size="x-small" color="primary">mdi-check</v-icon>
              </template>
              <v-list-item-title>{{ sanitizeHtml(item) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
  <v-alert v-else type="info" variant="tonal" class="mt-4">
    No additional options available for this trim. All features come standard.
  </v-alert>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { TrimData } from '@/types'

const props = defineProps<{
  trim: TrimData | null
}>()

const getCategoryIcon = (categoryName: string): string => {
  const name = categoryName.toLowerCase()
  if (name.includes('package') || name.includes('equipment group'))
    return 'mdi-package-variant'
  if (name.includes('safety') || name.includes('emission'))
    return 'mdi-shield-plus'
  if (name.includes('comfort') || name.includes('seat'))
    return 'mdi-seat-passenger'
  if (
    name.includes('technology') ||
    name.includes('radio') ||
    name.includes('infotainment')
  )
    return 'mdi-cellphone-link'
  if (
    name.includes('exterior') ||
    name.includes('paint') ||
    name.includes('wheel')
  )
    return 'mdi-car-side'
  if (name.includes('interior') || name.includes('trim')) return 'mdi-sofa'
  if (
    name.includes('engine') ||
    name.includes('transmission') ||
    name.includes('axle')
  )
    return 'mdi-cog'
  return 'mdi-tune'
}

const formatCategoryName = (name: string): string => {
  // If the name already has spaces or is well-formatted, return as is
  if (
    name.includes(' ') ||
    name === name.toLowerCase() ||
    name === name.toUpperCase()
  ) {
    // Capitalize first letter of each word
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }
  // Convert camelCase/PascalCase to readable format
  return name
    .split(/(?=[A-Z])/)
    .join(' ')
    .replace(/\b\w/g, l => l.toUpperCase())
}

const options = computed(() => {
  if (!props.trim?.availableOptions) return {}

  const result: Record<
    string,
    { title: string; icon: string; items: string[] }
  > = {}

  // Iterate through all available option categories from the API
  Object.entries(props.trim.availableOptions).forEach(([key, optionData]) => {
    if (optionData?.items && optionData.items.length > 0) {
      result[key] = {
        title: formatCategoryName(key),
        icon: getCategoryIcon(key),
        items: optionData.items,
      }
    }
  })

  return result
})

const sanitizeHtml = (text: string): string => {
  if (!text) return ''
  return text
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const hasOptions = computed(() => {
  return Object.values(options.value).some(
    category => category.items.length > 0,
  )
})
</script>

<style scoped>
:deep(.v-list-item-title) {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}
</style>
